import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type RefObject } from 'react'
import { nanoid } from 'nanoid'
import { useStore } from 'react-redux'
import { isFileAcceptedForUserFilesUpload } from '@/user_files/utils/file-type-utils'
import { validateUserFilesUploadFile } from '@/user_files/utils/upload-validation'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import { useUppyUpload } from '@/uppy-file-upload/hooks/useUppyUpload'
import { selectFileListRequest, selectFilesForFolder } from '@/user_files/files/store/selectors'
import { resolveUploadName } from '@/user_files/drive/utils/resolve-upload-name'
import { RootState } from '@/store/store'
import type { Conflict } from '@/user_files/drive/components/ConflictModal'
import { UserFilesActions } from '@/user_files/files/store/slice'

type PendingConflict = Conflict & { clientTmpId: string; file: File }

type UseDriveUploadParams = {
  folderId: string | null
  isFileAccepted?: (file: File) => boolean
  validateUploadFile?: (file: File) => Promise<string | null>
  onFileInitialized?: (params: { clientTmpId: string; serverFileId: string; storagePath: string; folderId: string }) => void
  onFileRegistered?: (params: { clientTmpId: string; folderId: string; filename: string }) => void
  onUploadCompleted?: (params: { clientTmpId: string; serverFileId?: string; folderId: string }) => void
  onUploadFailed?: (params: { clientTmpId: string; serverFileId?: string; folderId: string; error: string }) => void
}

type UseDriveUploadReturn = {
  /** Opens the upload picker. */
  openUploadPicker: () => void
  /** Whether the upload is disabled. */
  disabled: boolean
  /** Ref to the hidden file input.
   * This allows us to support uploading files from outside of the Dropzone. */
  fileInputRef: RefObject<HTMLInputElement>
  /** Adds files to the upload queue. */
  addFiles: (files: File[]) => void
  /** When the user picks file(s) via the OS dialog. */
  onFilesPicked: (e: ChangeEvent<HTMLInputElement>) => void
  /** Cancels an upload. */
  cancelUpload: (clientTmpId: string) => void
  /** Removes a file from Redux (queued row or finished processing row). */
  removeFile: (clientTmpId: string) => void
  /** Whether there are queued files. */
  hasQueuedFiles: boolean
  /** State and handlers for the filename conflict resolution modal. */
  conflictDialog: {
    /** Whether the conflict modal is open. */
    open: boolean
    /** One entry per file whose name collides with an existing file in the folder. */
    conflicts: Conflict[]
    /** Names already taken in this folder — used for per-field validation in the modal. */
    occupiedNames: ReadonlySet<string>
    /** Proceed with the (possibly user-edited) resolved names for conflicting files. */
    onConfirm: (resolvedNames: string[]) => void
    /** Discard all files that had name conflicts. */
    onCancel: () => void
  }
}

function getAddFileErrorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Unable to upload file.'
}

function getWaitingFileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}:${file.type}`
}

/**
 * Drive upload mechanics: Uppy + hidden file input + auto-start.
 *
 * Files arrive via drag/drop (`addFiles`) or OS picker (`onFilesPicked`)
 */
export function useDriveUpload({
  folderId,
  isFileAccepted = isFileAcceptedForUserFilesUpload,
  validateUploadFile = validateUserFilesUploadFile,
  onFileInitialized,
  onFileRegistered,
  onUploadCompleted,
  onUploadFailed,
}: UseDriveUploadParams): UseDriveUploadReturn {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const ownedClientTmpIdsRef = useRef<Set<string>>(new Set())
  const lastProcessedWaitingFilesRef = useRef<File[] | null>(null)
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const fileListRequest = useAppSelector((state) => selectFileListRequest(state, { folderId }))

  const [pendingConflicts, setPendingConflicts] = useState<PendingConflict[]>([])
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
  const [occupiedNamesSnapshot, setOccupiedNamesSnapshot] = useState<ReadonlySet<string>>(new Set())
  const [filesWaitingForFolderFiles, setFilesWaitingForFolderFiles] = useState<File[]>([])

  const { uppy, queuedFiles, allFiles, startUpload, cancelUpload, removeFile } = useUppyUpload({
    onFileInitialized,
    onUploadCompleted,
    onUploadFailed,
  })

  const ownedQueuedFiles = useMemo(() => queuedFiles.filter((file) => ownedClientTmpIdsRef.current.has(file.client.clientTmpId)), [queuedFiles])
  const ownedFilesAreUploading = useMemo(
    () =>
      allFiles.some(
        (file) =>
          ownedClientTmpIdsRef.current.has(file.client.clientTmpId) && (file.client.uploadStatus === 'initializing' || file.client.uploadStatus === 'uploading')
      ),
    [allFiles]
  )
  const disabled = !folderId || ownedFilesAreUploading

  const registerOwnedUpload = useCallback(
    ({ clientTmpId, filename }: { clientTmpId: string; filename: string }) => {
      if (!folderId) return
      const isNewUploadForOwner = !ownedClientTmpIdsRef.current.has(clientTmpId)
      ownedClientTmpIdsRef.current.add(clientTmpId)
      if (isNewUploadForOwner) {
        onFileRegistered?.({ clientTmpId, folderId, filename })
      }
    },
    [folderId, onFileRegistered]
  )

  const addToUppy = useCallback(
    (file: File, name: string, clientTmpId: string) => {
      try {
        uppy.addFile({ name, type: file.type, data: file, source: 'Local', meta: { clientTmpId, folderId } })
      } catch (error) {
        console.error('Error adding file:', error)
        if (folderId) {
          dispatch(
            UppyFileUploadActions.validationFailedFileAdded({
              clientTmpId,
              filename: name,
              fileSize: file.size,
              folderId,
              error: getAddFileErrorMessage(error),
            })
          )
        }
      }
    },
    [uppy, folderId, dispatch]
  )

  const validateAndAddToUppy = useCallback(
    async (candidates: Array<{ file: File; name: string; clientTmpId: string }>) => {
      if (!folderId) return
      const validated: Array<{ file: File; name: string; clientTmpId: string }> = []

      for (const { file, name, clientTmpId } of candidates) {
        let validationError: string | null = null
        try {
          validationError = await validateUploadFile(file)
        } catch (error) {
          console.error('Error validating file:', error)
          validationError = 'Unable to validate file.'
        }

        if (validationError) {
          dispatch(
            UppyFileUploadActions.validationFailedFileAdded({
              clientTmpId,
              filename: name,
              fileSize: file.size,
              folderId,
              error: validationError,
            })
          )
          continue
        }

        validated.push({ file, name, clientTmpId })
      }

      for (const { file, name, clientTmpId } of validated) {
        addToUppy(file, name, clientTmpId)
      }
    },
    [folderId, dispatch, addToUppy, validateUploadFile]
  )

  const addFilesToUppy = useCallback(
    (files: File[]) => {
      if (!folderId) return
      if (files.length === 0) return

      // lazy load the folder files for conflict detection if the list is not already loaded; do not add files to uppy until the list is loaded
      if (fileListRequest.status !== 'success') {
        lastProcessedWaitingFilesRef.current = null
        setFilesWaitingForFolderFiles((currentFiles) => [...currentFiles, ...files])
        if (fileListRequest.status !== 'loading') {
          dispatch(UserFilesActions.fetchFilesForFolder({ folderId }))
        }
        return
      }

      // Build the set of names already taken in this folder (existing + queued uploads).
      const state = store.getState()
      const existingFiles = selectFilesForFolder(state, { folderId })
      const occupiedNames = new Set<string>(existingFiles.map((f) => f.original_filename))
      Object.values(state.uppyFileUploadState.files)
        .filter((f) => f.client.folderId === folderId && f.client.uploadStatus !== 'error' && f.client.uploadStatus !== 'unsupported_type')
        .forEach((f) => occupiedNames.add(f.client.filename))

      const conflicts: PendingConflict[] = []
      const uploadCandidates: Array<{ file: File; name: string; clientTmpId: string }> = []

      for (const file of files) {
        const clientTmpId = nanoid()

        if (!isFileAccepted(file)) {
          registerOwnedUpload({ clientTmpId, filename: file.name })
          dispatch(
            UppyFileUploadActions.unsupportedTypeFileAdded({
              clientTmpId,
              filename: file.name,
              fileSize: file.size,
              folderId,
            })
          )
          continue
        }

        if (occupiedNames.has(file.name)) {
          const resolvedName = resolveUploadName(file.name, occupiedNames)
          // Add the resolved name to occupiedNames so batch drops don't re-use it,
          // but snapshot the base set (before resolved names) for modal validation.
          occupiedNames.add(resolvedName)
          conflicts.push({ clientTmpId, file, originalName: file.name, resolvedName })
        } else {
          occupiedNames.add(file.name)
          registerOwnedUpload({ clientTmpId, filename: file.name })
          dispatch(
            UppyFileUploadActions.fileValidationStarted({
              clientTmpId,
              filename: file.name,
              fileSize: file.size,
              folderId,
            })
          )
          uploadCandidates.push({ file, name: file.name, clientTmpId })
        }
      }

      if (conflicts.length > 0) {
        // Snapshot names taken by existing files + non-conflicting uploads from this drop.
        // Excludes the conflict resolved names so the user can freely choose any of those slots.
        const baseOccupied = new Set(occupiedNames)
        for (const c of conflicts) baseOccupied.delete(c.resolvedName)
        setPendingConflicts(conflicts)
        setOccupiedNamesSnapshot(baseOccupied)
        setConflictDialogOpen(true)
      }

      void validateAndAddToUppy(uploadCandidates)
    },
    [dispatch, fileListRequest.status, folderId, store, validateAndAddToUppy, isFileAccepted, registerOwnedUpload]
  )

  const getUniqueWaitingFiles = useCallback((files: File[]) => {
    if (lastProcessedWaitingFilesRef.current === files) return []
    lastProcessedWaitingFilesRef.current = files

    const waitingFileKeys = new Set<string>()
    const uniqueFiles: File[] = []
    for (const file of files) {
      const fileKey = getWaitingFileKey(file)
      if (waitingFileKeys.has(fileKey)) continue
      waitingFileKeys.add(fileKey)
      uniqueFiles.push(file)
    }

    return uniqueFiles
  }, [])

  useEffect(() => {
    setFilesWaitingForFolderFiles([])
    lastProcessedWaitingFilesRef.current = null
  }, [folderId])

  useEffect(() => {
    if (fileListRequest.status === 'error') {
      if (filesWaitingForFolderFiles.length === 0) return
      const waitingFilesBatch = filesWaitingForFolderFiles
      const files = getUniqueWaitingFiles(waitingFilesBatch)
      if (files.length === 0) return

      if (folderId) {
        for (const file of files) {
          const clientTmpId = nanoid()
          registerOwnedUpload({ clientTmpId, filename: file.name })
          dispatch(
            UppyFileUploadActions.validationFailedFileAdded({
              clientTmpId,
              filename: file.name,
              fileSize: file.size,
              folderId,
              error: 'Matter Files could not be loaded. Try again.',
            })
          )
        }
      }
      setFilesWaitingForFolderFiles((currentFiles) => (currentFiles === waitingFilesBatch ? [] : currentFiles))
    }
  }, [dispatch, fileListRequest.status, filesWaitingForFolderFiles, folderId, registerOwnedUpload, getUniqueWaitingFiles])

  // when the folder files are loaded and ready for name conflict detection, add the waiting files to the uppy queue
  useEffect(() => {
    if (fileListRequest.status !== 'success') return
    if (filesWaitingForFolderFiles.length === 0) return

    const waitingFilesBatch = filesWaitingForFolderFiles
    const files = getUniqueWaitingFiles(waitingFilesBatch)
    if (files.length === 0) return

    setFilesWaitingForFolderFiles((currentFiles) => (currentFiles === waitingFilesBatch ? [] : currentFiles))
    addFilesToUppy(files)
  }, [addFilesToUppy, fileListRequest.status, filesWaitingForFolderFiles, getUniqueWaitingFiles])

  const confirmConflictUpload = useCallback(
    (resolvedNames: string[]) => {
      const candidates = pendingConflicts.map((conflict, i) => ({
        file: conflict.file,
        name: resolvedNames[i] ?? conflict.resolvedName,
        clientTmpId: conflict.clientTmpId,
      }))

      setPendingConflicts([])
      setConflictDialogOpen(false)

      if (!folderId) return
      for (const { clientTmpId, name, file } of candidates) {
        registerOwnedUpload({ clientTmpId, filename: name })
        dispatch(
          UppyFileUploadActions.fileValidationStarted({
            clientTmpId,
            filename: name,
            fileSize: file.size,
            folderId,
          })
        )
      }

      void validateAndAddToUppy(candidates)
    },
    [pendingConflicts, folderId, dispatch, validateAndAddToUppy, registerOwnedUpload]
  )

  const cancelConflictUpload = useCallback(() => {
    setPendingConflicts([])
    setConflictDialogOpen(false)
  }, [])

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : []
      addFilesToUppy(files)
      e.target.value = ''
    },
    [addFilesToUppy]
  )

  const openUploadPicker = useCallback(() => {
    if (disabled) return
    fileInputRef.current?.click()
  }, [disabled])

  const hasQueuedFiles = ownedQueuedFiles.length > 0

  useEffect(() => {
    if (hasQueuedFiles && !ownedFilesAreUploading) {
      startUpload(ownedQueuedFiles)
    }
  }, [hasQueuedFiles, ownedFilesAreUploading, ownedQueuedFiles, startUpload])

  const cancelOwnedUpload = useCallback(
    (clientTmpId: string) => {
      ownedClientTmpIdsRef.current.delete(clientTmpId)
      cancelUpload(clientTmpId)
    },
    [cancelUpload]
  )

  const removeOwnedFile = useCallback(
    (clientTmpId: string) => {
      ownedClientTmpIdsRef.current.delete(clientTmpId)
      removeFile(clientTmpId)
    },
    [removeFile]
  )

  const conflictDialogConflicts = useMemo(() => pendingConflicts.map(({ originalName, resolvedName }) => ({ originalName, resolvedName })), [pendingConflicts])

  return {
    openUploadPicker,
    disabled,
    fileInputRef,
    addFiles: addFilesToUppy,
    onFilesPicked: handleFileSelect,
    cancelUpload: cancelOwnedUpload,
    removeFile: removeOwnedFile,
    hasQueuedFiles,
    conflictDialog: {
      open: conflictDialogOpen,
      conflicts: conflictDialogConflicts,
      occupiedNames: occupiedNamesSnapshot,
      onConfirm: confirmConflictUpload,
      onCancel: cancelConflictUpload,
    },
  }
}
